import { useState, useRef, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import type { FieldError } from 'react-hook-form';
import { Search } from 'lucide-react';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface FormComboboxProps {
  label: string;
  options: ComboboxOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  error?: FieldError;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  name?: string;
}

export const FormCombobox = forwardRef<HTMLInputElement, FormComboboxProps>(
  (
    { label, options, value = '', onChange, onBlur, error, required, placeholder, helperText, name },
    ref,
  ) => {
    const [query, setQuery] = useState(value);
    const [open, setOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const containerRef = useRef<HTMLDivElement>(null);
    const inputId = name ?? 'combobox';

    // Keep query in sync when value changes externally (e.g. SSO auto-fill)
    useEffect(() => {
      setQuery(value);
    }, [value]);

    function updateDropdownPosition() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 9999,
        });
      }
    }

    // Close on outside click
    useEffect(() => {
      function handleOutsideClick(e: MouseEvent) {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
          onBlur?.();
        }
      }
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [onBlur]);

    // Reposition if the page scrolls while open
    useEffect(() => {
      if (!open) return;
      function handleScroll() {
        updateDropdownPosition();
      }
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }, [open]);

    const filtered = query.trim()
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(query.toLowerCase()) ||
            o.value.toLowerCase().includes(query.toLowerCase()),
        )
      : options;

    function select(opt: ComboboxOption) {
      setQuery(opt.value);
      onChange?.(opt.value);
      setOpen(false);
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
      setQuery(e.target.value);
      onChange?.(e.target.value);
      setOpen(true);
    }

    function handleFocus() {
      updateDropdownPosition();
      setOpen(true);
    }

    function handleKeyDown(e: React.KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        onBlur?.();
      }
    }

    const dropdown =
      open && filtered.length > 0
        ? createPortal(
            <ul
              role="listbox"
              style={dropdownStyle}
              className="bg-white border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto"
            >
              {filtered.map((opt) => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  onMouseDown={() => select(opt)}
                  className={`
                    px-4 py-2.5 cursor-pointer transition-colors bg-white
                    hover:bg-gray-50
                    ${opt.value === value ? 'bg-gray-50' : ''}
                  `}
                >
                  <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500">{opt.value}</p>
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null;

    return (
      <div className="space-y-2" ref={containerRef}>
        <label htmlFor={inputId} className="block text-sm font-medium text-[var(--foreground)]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>

        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            name={name}
            type="text"
            autoComplete="off"
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            aria-expanded={open}
            aria-haspopup="listbox"
            role="combobox"
            className={`
              w-full px-4 py-3 pr-10
              bg-[var(--surface)] border rounded-xl
              text-[var(--foreground)] placeholder:text-[var(--surface-muted)]
              focus:outline-none focus:ring-2 focus:ring-interactive focus:border-transparent
              transition-all duration-200
              ${error ? 'border-red-500' : 'border-[var(--surface-border)]'}
            `}
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--surface-muted)] pointer-events-none" />
        </div>

        {dropdown}

        {error && (
          <p id={`${inputId}-error`} className="text-sm text-red-400">
            {error.message}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="text-sm text-[var(--surface-muted)]">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

FormCombobox.displayName = 'FormCombobox';
